import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  Divider,
  Skeleton,
  Fade,
  Slide,
  Badge,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  Cancel as CancelIcon,
  CheckCircle as ApproveIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  MonetizationOn as PaymentIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon // Added for history button
} from '@material-ui/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom'; // Added for navigation
import admin_api from '../../../api/admin';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

// Custom styles using makeStyles
const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
    marginTop: '80px',
    marginRight: '24px',
    transition: 'all 0.3s ease',
  },
  paper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  card: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    height: '100%',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: theme.spacing(1),
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#06b6d4',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#06b6d4',
    },
    '& .MuiOutlinedInput-input': {
      color: '#e2e8f0',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#06b6d4',
    },
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2235',
      color: '#ffffff',
      borderRadius: theme.spacing(2),
      border: '1px solid #2d3748',
    },
  },
  headerCard: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(239, 68, 68, 0.3)',
  },
  successButton: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
    },
  },
  errorButton: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    },
  },
  // New style for history button
  historyButton: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
    },
  },
  tableHead: {
    backgroundColor: '#1e293b',
    color: '#06b6d4',
    fontWeight: 'bold',
    borderBottom: '2px solid #2d3748'
  },
  tableRow: {
    '& .MuiTableCell-root': {
      color: '#e2e8f0',
      borderColor: '#2d3748'
    },
    '&:hover': {
      backgroundColor: 'rgba(6, 182, 212, 0.05)'
    }
  },
  pagination: {
    color: '#e2e8f0',
    borderTop: '1px solid #2d3748',
    '& .MuiTablePagination-selectIcon': {
      color: '#e2e8f0'
    },
    '& .MuiTablePagination-actions button': {
      color: '#e2e8f0'
    }
  },
  avatar: {
    backgroundColor: '#ef4444',
    width: 32,
    height: 32,
    fontSize: '0.8rem'
  },
  chip: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    borderColor: '#ef4444',
    border: '1px solid'
  },
  alertSuccess: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    color: '#22c55e',
    border: '1px solid rgba(34, 197, 94, 0.2)'
  },
  alertWarning: {
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#f59e0b',
    border: '1px solid rgba(245, 158, 11, 0.2)'
  }
}));

export default function CancellationManagement() {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate(); // Added for navigation
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const { isCollapse } = useCollapseDrawer();
  
  // Calculate container margins
  const containerStyle = {
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  // Use useCallback to prevent infinite re-renders
  const fetchCancellationRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await admin_api.get_cancellation_requests(page + 1, rowsPerPage, statusFilter);
      if (response && response.data && response.data.success) {
        const requests = response.data.data || [];
        setCancellationRequests(requests);
        setFilteredRequests(requests);
        setTotalCount(response.data.pagination?.totalRecords || requests.length);
      } else {
        console.warn('[CancellationManagement] API returned unsuccessful response:', response);
        setError(response?.data?.message || 'Failed to fetch cancellation requests');
        setCancellationRequests([]);
        setFilteredRequests([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('[CancellationManagement] Error in fetchCancellationRequests:', error);
      setError('Failed to fetch cancellation requests. Please try again.');
      setCancellationRequests([]);
      setFilteredRequests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]); // Only depend on these values

  // Filter requests based on search - separate useEffect to avoid refetching
  useEffect(() => {
    let filtered = [...cancellationRequests];
    
    if (searchQuery) {
      filtered = filtered.filter(request => 
        request.booking_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.court_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredRequests(filtered);
  }, [cancellationRequests, searchQuery]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchCancellationRequests();
  }, [fetchCancellationRequests]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setOpenDetailsDialog(true);
  };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setAdminNotes('');
    setRefundAmount(request.paid_amount || '0');
    setOpenApproveDialog(true);
  };

  const handleDeclineClick = (request) => {
    setSelectedRequest(request);
    setAdminNotes('');
    setOpenDeclineDialog(true);
  };

  // New function to handle navigation to history page
  const handleViewHistory = () => {
    navigate('/admin/cancellations/history');
  };

  const handleApproveConfirm = async () => {
    if (!selectedRequest) return;
    
    setActionLoading(true);
    try {
      const requestData = {
        requestId: selectedRequest.id,
        action: 'approve',
        adminNotes: adminNotes || 'Cancellation approved',
        refundAmount: parseFloat(refundAmount) || 0
      };
      const response = await admin_api.process_cancellation_request(requestData);
      
      if (response && response.data && response.data.success) {
        setSuccessMessage('Cancellation request approved successfully');
        setOpenApproveDialog(false);
        // Refresh the data
        fetchCancellationRequests();
      } else {
        setError(response?.data?.message || 'Failed to approve cancellation request');
      }
    } catch (error) {
      console.error('[CancellationManagement] Error in handleApproveConfirm:', error);
      setError(error.message || 'Failed to approve cancellation request. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!selectedRequest) return;
    
    if (!adminNotes) {
      setError('Please provide a reason for declining the cancellation request');
      return;
    }
    
    setActionLoading(true);
    try {
      const requestData = {
        requestId: selectedRequest.id,
        action: 'reject',
        adminNotes: adminNotes
      };
      const response = await admin_api.process_cancellation_request(requestData);
      
      if (response && response.data && response.data.success) {
        setSuccessMessage('Cancellation request declined successfully');
        setOpenDeclineDialog(false);
        fetchCancellationRequests();
      } else {
        setError(response?.data?.message || 'Failed to decline cancellation request');
      }
    } catch (error) {
      console.error('[CancellationManagement] Error in handleDeclineConfirm:', error);
      setError(error.message || 'Failed to decline cancellation request. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const clearMessages = () => {
    setSuccessMessage('');
    setError(null);
  };

  const formatDateToPH = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).utcOffset('+08:00').format('MMM D, YYYY');
  };

  const convertTo12Hour = (timeString) => {
    if (!timeString) return '';
    
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString;
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getCancelTypeDisplay = (cancelType) => {
    const types = {
      emergency: 'Emergency',
      change_plans: 'Change of Plans',
      double_booking: 'Double Booking',
      weather: 'Weather',
      other: 'Other'
    };
    return types[cancelType] || cancelType;
  };

  // Calculate summary statistics
  const summaryStats = {
    pending: filteredRequests.filter(r => r.status === 'pending').length,
    todayRequests: filteredRequests.filter(r => {
      const requestDate = r.requested_at || r.created_at;
      if (!requestDate) return false;
      
      const todayPH = new Date().toLocaleString('en-US', {timeZone: 'Asia/Manila'});
      const today = new Date(todayPH);
      
      const date = new Date(requestDate);
      const datePH = new Date(date.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
      
      return datePH.getDate() === today.getDate() && 
             datePH.getMonth() === today.getMonth() && 
             datePH.getFullYear() === today.getFullYear();
    }).length,
    totalRefundValue: filteredRequests
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + parseFloat(r.refund_amount || 0), 0)
  };

  return (
    <Box className={classes.container} style={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" style={{ backgroundColor: '#22c55e' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" style={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <Card className={classes.headerCard}>
            <CardContent style={{ padding: theme.spacing(4) }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                    Cancellation Management
                  </Typography>
                  <Typography variant="h6" style={{ opacity: 0.9 }}>
                    Review and process booking cancellation requests
                  </Typography>
                </Box>
                {/* Updated header buttons section with History button */}
                <Box style={{ display: 'flex', gap: 16 }}>
                  <Button
                    variant="contained"
                    className={classes.historyButton}
                    startIcon={<HistoryIcon />}
                    onClick={handleViewHistory}
                  >
                    View History
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchCancellationRequests}
                    disabled={loading}
                    style={{
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: theme.spacing(3),
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={3} style={{ marginBottom: theme.spacing(4) }}>
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1000}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <Badge badgeContent={summaryStats.pending} color="error" max={99}>
                      <WarningIcon style={{ fontSize: 40, color: '#ef4444', marginBottom: 16 }} />
                    </Badge>
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Pending Requests
                    </Typography>
                    <Typography variant="h4" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                      {summaryStats.pending}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Awaiting your decision
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1200}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <ScheduleIcon style={{ fontSize: 40, color: '#f59e0b', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Today's Requests
                    </Typography>
                    <Typography variant="h4" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {summaryStats.todayRequests}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Submitted today
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1400}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <MoneyIcon style={{ fontSize: 40, color: '#22c55e', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Total Refunds
                    </Typography>
                    <Typography variant="h4" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                      ₱{summaryStats.totalRefundValue.toLocaleString()}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Approved refunds
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          </Grid>

          {/* Filters and Search */}
          <Paper className={classes.paper} style={{ padding: theme.spacing(3), marginBottom: theme.spacing(4) }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={classes.textField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#06b6d4' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel style={{ color: '#a0aec0' }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#e2e8f0',
                    }}
                    inputProps={{
                      style: {
                        color: '#e2e8f0',
                      }
                    }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="all">All Requests</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchCancellationRequests}
                    disabled={loading}
                    style={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                    }}
                  >
                    Refresh
                  </Button>
                  <Typography 
                    variant="body2" 
                    style={{ color: '#a0aec0', alignSelf: 'center' }}
                  >
                    {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''} found
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Rest of the component remains the same... */}
          {/* Requests Table */}
          <Slide direction="up" in timeout={1600}>
            <TableContainer component={Paper} className={classes.paper}>
              {loading ? (
                <Box style={{ padding: theme.spacing(4) }}>
                  {[...Array(5)].map((_, index) => (
                    <Box key={index} style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      <Skeleton variant="circle" width={40} height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="30%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="40%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="20%" height={40} style={{ backgroundColor: '#2d3748' }} />
                    </Box>
                  ))}
                </Box>
              ) : filteredRequests.length === 0 ? (
                <Box style={{ padding: theme.spacing(6), textAlign: 'center' }}>
                  <AssignmentIcon style={{ fontSize: 60, color: '#4a5568', marginBottom: 16 }} />
                  <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: 8 }}>
                    No cancellation requests found
                  </Typography>
                  <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: 24 }}>
                    {searchQuery || statusFilter !== 'pending' 
                      ? 'No requests match your current filters. Try adjusting your search criteria.'
                      : 'No cancellation requests have been submitted yet.'}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={fetchCancellationRequests}
                    style={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                    }}
                  >
                    Refresh List
                  </Button>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHead}>Customer</TableCell>
                      <TableCell className={classes.tableHead}>Booking</TableCell>
                      <TableCell className={classes.tableHead}>Court & Date</TableCell>
                      <TableCell className={classes.tableHead}>Reason</TableCell>
                      <TableCell className={classes.tableHead}>Requested</TableCell>
                      <TableCell className={classes.tableHead}>Status</TableCell>
                      <TableCell className={classes.tableHead} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((request, index) => (
                      <Fade in timeout={300 + index * 100} key={request.id}>
                        <TableRow className={classes.tableRow}>
                          <TableCell>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <Avatar className={classes.avatar}>
                                {(request.user_name || 'U').charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                  {request.user_name || 'Unknown User'}
                                </Typography>
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {request.user_email || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {request.booking_id}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              #{request.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                {request.court_name}
                              </Typography>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <EventIcon style={{ fontSize: 14, color: '#06b6d4' }} />
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {formatDateToPH(request.reservation_date)}
                                </Typography>
                              </Box>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <TimeIcon style={{ fontSize: 14, color: '#06b6d4' }} />
                                <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                  {convertTo12Hour(request.time_from)} - {convertTo12Hour(request.time_to)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {getCancelTypeDisplay(request.cancel_type)}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#a0aec0' }}>
                              {request.reason.length > 50 ? `${request.reason.substring(0, 50)}...` : request.reason}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ color: '#a0aec0' }}>
                              {moment(request.requested_at).utcOffset('+08:00').fromNow()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={request.status.toUpperCase()} 
                              size="small" 
                              style={{
                                backgroundColor: request.status === 'pending' 
                                  ? 'rgba(245, 158, 11, 0.1)' 
                                  : request.status === 'approved'
                                    ? 'rgba(34, 197, 94, 0.1)'
                                    : 'rgba(239, 68, 68, 0.1)',
                                color: request.status === 'pending' 
                                  ? '#f59e0b' 
                                  : request.status === 'approved'
                                    ? '#22c55e'
                                    : '#ef4444',
                                border: `1px solid ${request.status === 'pending' 
                                  ? '#f59e0b' 
                                  : request.status === 'approved'
                                    ? '#22c55e'
                                    : '#ef4444'}`
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                              <Tooltip title="View Details">
                                <IconButton 
                                  style={{ color: '#06b6d4' }}
                                  onClick={() => handleViewDetails(request)}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              {request.status === 'pending' && (
                                <>
                                  <Tooltip title="Approve Request">
                                    <IconButton 
                                      style={{ color: '#22c55e' }}
                                      onClick={() => handleApproveClick(request)}
                                    >
                                      <ApproveIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Decline Request">
                                    <IconButton 
                                      style={{ color: '#ef4444' }}
                                      onClick={() => handleDeclineClick(request)}
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {filteredRequests.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredRequests.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  className={classes.pagination}
                />
              )}
            </TableContainer>
          </Slide>
        </Box>
      </Fade>

      {/* Request Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        {selectedRequest && (
          <>
            <DialogTitle style={{ 
              borderBottom: '1px solid #2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: 16
            }}>
              <ViewIcon style={{ color: '#06b6d4' }} />
              Cancellation Request Details
            </DialogTitle>
            <DialogContent dividers style={{ padding: theme.spacing(4) }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#06b6d4', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Request Information
                    </Typography>
                    
                    <Box style={{ marginBottom: 24 }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Request ID:</Typography>
                        <Typography variant="body1" style={{ fontWeight: 'medium' }}>
                          #{selectedRequest.id}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Cancellation Type:</Typography>
                        <Typography variant="body1">
                          {getCancelTypeDisplay(selectedRequest.cancel_type)}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Customer:</Typography>
                        <Typography variant="body1">
                          {selectedRequest.user_name}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Email:</Typography>
                        <Typography variant="body1">
                          {selectedRequest.user_email}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Reason for Cancellation:</Typography>
                      <Box style={{ 
                        backgroundColor: 'rgba(6, 182, 212, 0.1)', 
                        padding: 12, 
                        borderRadius: 4,
                        border: '1px solid rgba(6, 182, 212, 0.2)'
                      }}>
                        <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                          {selectedRequest.reason}
                        </Typography>
                      </Box>
                      
                      {selectedRequest.additional_notes && (
                        <Box style={{ marginTop: 16 }}>
                          <Typography variant="subtitle2" gutterBottom>Additional Notes:</Typography>
                          <Box style={{ 
                            backgroundColor: 'rgba(139, 92, 246, 0.1)', 
                            padding: 12, 
                            borderRadius: 4,
                            border: '1px solid rgba(139, 92, 246, 0.2)'
                          }}>
                            <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                              {selectedRequest.additional_notes}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#06b6d4', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Booking Details
                    </Typography>
                    
                    <Box style={{ marginBottom: 16 }}>
                      <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <EventIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                        <Typography>
                          <strong>Booking ID:</strong> {selectedRequest.booking_id}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <LocationIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                        <Typography>
                          <strong>Court:</strong> {selectedRequest.court_name}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <EventIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                        <Typography>
                          <strong>Date:</strong> {formatDateToPH(selectedRequest.reservation_date)}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <TimeIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                        <Typography>
                          <strong>Time:</strong> {convertTo12Hour(selectedRequest.time_from)} - {convertTo12Hour(selectedRequest.time_to)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box style={{ paddingTop: 16, borderTop: '1px solid #2d3748', marginTop: 16 }}>
                      <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold', color: '#06b6d4' }}>
                        Financial Information
                      </Typography>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={7}>
                          <Typography>Total Amount:</Typography>
                        </Grid>
                        <Grid item xs={5} style={{ textAlign: 'right' }}>
                          <Typography style={{ fontWeight: 'bold', color: '#22c55e' }}>
                            ₱{parseFloat(selectedRequest.total_amount || 0).toLocaleString()}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={7}>
                          <Typography>Amount Paid:</Typography>
                        </Grid>
                        <Grid item xs={5} style={{ textAlign: 'right' }}>
                          <Typography style={{ fontWeight: 'bold', color: '#f59e0b' }}>
                            ₱{parseFloat(selectedRequest.paid_amount || 0).toLocaleString()}
                          </Typography>
                        </Grid>
                        
                        {selectedRequest.status === 'approved' && (
                          <>
                            <Grid item xs={7}>
                              <Typography>Refund Amount:</Typography>
                            </Grid>
                            <Grid item xs={5} style={{ textAlign: 'right' }}>
                              <Typography style={{ fontWeight: 'bold', color: '#06b6d4' }}>
                                ₱{parseFloat(selectedRequest.refund_amount || 0).toLocaleString()}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748', gap: 16 }}>
              <Button 
                onClick={() => setOpenDetailsDialog(false)}
                style={{ color: '#a0aec0' }}
              >
                Close
              </Button>
              {selectedRequest.status === 'pending' && (
                <>
                  <Button 
                    className={classes.successButton}
                    variant="contained"
                    startIcon={<ApproveIcon />}
                    onClick={() => {
                      setOpenDetailsDialog(false);
                      handleApproveClick(selectedRequest);
                    }}
                  >
                    Approve
                  </Button>
                  <Button 
                    className={classes.errorButton}
                    variant="contained"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setOpenDetailsDialog(false);
                      handleDeclineClick(selectedRequest);
                    }}
                  >
                    Decline
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Approve Dialog */}
      <Dialog
        open={openApproveDialog}
        onClose={() => !actionLoading && setOpenApproveDialog(false)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        <DialogTitle style={{ 
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          color: '#22c55e'
        }}>
          <ApproveIcon />
          Approve Cancellation Request
        </DialogTitle>
        <DialogContent style={{ padding: theme.spacing(3) }}>
          <Alert 
            severity="success" 
            className={classes.alertSuccess}
          >
            You are about to approve this cancellation request. The booking will be cancelled and the customer will be notified.
          </Alert>
          
          <TextField
            fullWidth
            label="Refund Amount (₱)"
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            variant="outlined"
            disabled={actionLoading}
            className={classes.textField}
            style={{ marginTop: 16, marginBottom: 16 }}
            helperText="Enter the refund amount to be processed for the customer"
          />
          
          <TextField
            fullWidth
            label="Admin Notes (Optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            disabled={actionLoading}
            className={classes.textField}
            placeholder="Add any notes about this approval..."
          />
        </DialogContent>
        <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748', gap: 16 }}>
          <Button 
            onClick={() => setOpenApproveDialog(false)} 
            disabled={actionLoading}
            style={{ color: '#a0aec0' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleApproveConfirm} 
            className={classes.successButton}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <ApproveIcon />}
          >
            {actionLoading ? 'Approving...' : 'Approve Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Decline Dialog */}
      <Dialog
        open={openDeclineDialog}
        onClose={() => !actionLoading && setOpenDeclineDialog(false)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        <DialogTitle style={{ 
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          color: '#ef4444'
        }}>
          <CancelIcon />
          Decline Cancellation Request
        </DialogTitle>
        <DialogContent style={{ padding: theme.spacing(3) }}>
          <Alert 
            severity="warning" 
            className={classes.alertWarning}
          >
            Please provide a clear reason for declining this cancellation request. This will be sent to the customer.
          </Alert>
          
          <TextField
            fullWidth
            label="Reason for Declining *"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={4}
            variant="outlined"
            required
            disabled={actionLoading}
            error={openDeclineDialog && !adminNotes}
            helperText={openDeclineDialog && !adminNotes ? 'A reason is required to decline a cancellation request' : 'This message will be sent to the customer'}
            className={classes.textField}
            placeholder="e.g., Request does not meet cancellation policy requirements, insufficient time before booking, etc."
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748', gap: 16 }}>
          <Button 
            onClick={() => setOpenDeclineDialog(false)} 
            disabled={actionLoading}
            style={{ color: '#a0aec0' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeclineConfirm} 
            className={classes.errorButton}
            variant="contained"
            disabled={actionLoading || !adminNotes}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
          >
            {actionLoading ? 'Declining...' : 'Decline Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}